import React from 'react';
import { Attachment } from 'stream-chat-react';
import BusinessCardAttachment from './BusinessCardAttachment';
import ScoutRequestShareCard from './ScoutRequestShareCard';
import ScoutRecommendationForwardCard from './ScoutRecommendationForwardCard';
import LastMinuteListingCard from './LastMinuteListingCard';
import FriendlyRequestCard from './FriendlyRequestCard';
import RadarPlayerShareCard from './RadarPlayerShareCard';
import RadarShortlistShareCard from './RadarShortlistShareCard';
import ClubRadarShortlistShareCard from './ClubRadarShortlistShareCard';
import FallbackShareCard from './FallbackShareCard';

// Stream's own renderer (<Attachment/>) handles these natively; any OTHER
// non-empty custom type we don't recognize would render an empty bubble, so it
// is routed to FallbackShareCard instead (design frame 1c-b).
const NATIVE_TYPES = new Set([
  'image',
  'file',
  'video',
  'audio',
  'giphy',
  'voiceRecording',
]);

// LIVE ENTRY POINT: CustomMessage (the Channel's Message component) fully
// replaces Stream's default message renderer, so the `Attachment={CustomAttachment}`
// Channel prop is never consulted by Stream. The only live caller is
// CustomMessage's per-attachment delegation, which forwards every attachment
// type it does NOT render inline (image/file/video/audio/business-card/url) as
// `attachments={[attachment]}`. Each custom share type is routed to its card
// below; anything unrecognised falls to FallbackShareCard (never an empty bubble).
const CustomAttachment = (props) => {
  const type = props.attachments?.[0]?.type;
  // The hyphen `business-card` variant is rendered inline by CustomMessage and
  // therefore never delegates here; this case stays as the routed alias for the
  // underscore `business_card` variant (which CustomMessage does not match), so
  // both spellings get the full BusinessCardAttachment card exactly once.
  if (type === 'business-card' || type === 'business_card') {
    return <BusinessCardAttachment {...props} />;
  }
  // Scout Requests (M4): two card types share the same attachment path.
  if (type === 'scout_request_share') {
    return <ScoutRequestShareCard {...props} />;
  }
  if (type === 'scout_recommendation_forward') {
    return <ScoutRecommendationForwardCard {...props} />;
  }
  // TP-Last-Minute (design 9d): the opportunity listing context card.
  if (type === 'last_minute_listing') {
    return <LastMinuteListingCard {...props} />;
  }
  // TP-Friendlies (design Frame 9, D-003): the friendly-request unfurl (Open/Closed).
  if (type === 'friendly_request') {
    return <FriendlyRequestCard {...props} />;
  }
  // TP Agent Radar (P3): single-player + shortlist-snapshot share cards.
  if (type === 'radar_player_share') {
    return <RadarPlayerShareCard {...props} />;
  }
  if (type === 'radar_shortlist_share') {
    return <RadarShortlistShareCard {...props} />;
  }
  // TP Club Radar (P3): the club shared-list snapshot card (own card, own CTA to
  // the club snapshot view — never bends the agent card).
  if (type === 'club_radar_shortlist_share') {
    return <ClubRadarShortlistShareCard {...props} />;
  }
  // Unknown / legacy custom share type → minimal text bubble with link, never an
  // empty bubble. Stream-native attachments (and og link previews, whose type is
  // empty) keep the default renderer.
  if (type && !NATIVE_TYPES.has(type)) {
    return <FallbackShareCard {...props} />;
  }
  return <Attachment {...props} />;
};

export default CustomAttachment; 